import { lazy } from "react"
import type { ComponentType, LazyExoticComponent, ReactNode } from "react"

import CRMLayout from "../Screens/CRM/CRMLayout"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RouteAccess = "protected" | "public" | "bare"

export type ModuleCategory = "ai-growth" | "content" | "ai-tools" | "playground" | "aix-infra" | "super-admin"

export interface RouteEntry {
  /** URL path, e.g. "/crm/campaigns/$id" */
  path: string
  /** Lazy-loaded screen component */
  component: LazyExoticComponent<ComponentType<any>>
  /** Access mode. Default: "protected" */
  access?: RouteAccess
  /** Layout wrapper applied inside the access wrapper (e.g. CRMLayout) */
  layout?: ComponentType<{ children: ReactNode }>
  /** TanStack Router validateSearch */
  validateSearch?: (search: Record<string, unknown>) => Record<string, unknown>

  // --- Navigation fields (omit for routes not shown in sidebar) -----------
  /** Sidebar category */
  category?: ModuleCategory
  /** Display label */
  label?: string
  /** Emoji icon */
  icon?: string
  /** Parent group label for nested nav items (e.g. "Aura Builder") */
  parentGroup?: string
  /** Group-level icon holder — not a navigable route itself */
  isGroupHeader?: boolean
  /** Appears in the system section (Home, Tools, Electron Native) */
  isSystem?: boolean
  /** Sort order within its category/group. Lower = first. Default 100. */
  order?: number
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const routeRegistry: RouteEntry[] = [
  // =========================================================================
  // System items
  // =========================================================================
  {
    path: "/",
    component: lazy(() => import("../Screens/HomeDashboardScreen/HomeDashboardScreen")),
    label: "Home",
    icon: "📊",
    isSystem: true,
    order: 0,
  },
  {
    path: "/my-work",
    component: lazy(() => import("../Screens/MyWorkScreen")),
    label: "My Work",
    icon: "✅",
    isSystem: true,
    order: 1,
  },
  {
    path: "/tools",
    component: lazy(() => import("../pages/Tools")),
    label: "Tools",
    icon: "🛠️",
    isSystem: true,
    order: 2,
  },
  {
    path: "/settings",
    component: lazy(() => import("../Screens/SettingsScreen")),
    label: "Settings",
    icon: "🔐",
    isSystem: true,
    order: 3,
  },
  {
    path: "/electron-native",
    component: lazy(() => import("../Screens/ElectronNativeScreen")),
    label: "Electron Native",
    icon: "⚡",
    isSystem: true,
    order: 4,
  },

  // =========================================================================
  // AI Growth
  // =========================================================================
  {
    path: "/crm",
    component: lazy(() => import("../Screens/CRM/CRMDashboard")),
    layout: CRMLayout,
    category: "ai-growth",
    label: "CRM",
    icon: "👥",
    order: 0,
  },
  {
    path: "/seo",
    component: lazy(() => import("../Screens/SEOScreen")),
    category: "ai-growth",
    label: "SEO Board",
    icon: "🔍",
    order: 1,
  },
  {
    path: "/reports",
    component: lazy(() => import("../Screens/ReportsScreen/ReportsScreen")),
    category: "ai-growth",
    label: "AIX Reports",
    icon: "📈",
    order: 2,
  },
  {
    path: "/email-marketing",
    component: lazy(() =>
      import("../Screens/EmailMarketingScreen/EmailMarketingScreen").then((m) => ({ default: m.EmailMarketingScreen })),
    ),
    category: "ai-growth",
    label: "Email Marketing",
    icon: "📧",
    order: 3,
  },

  // CRM sub-routes (not in nav)
  {
    path: "/crm/campaigns",
    component: lazy(() => import("../Screens/CRM/CampaignsList")),
    layout: CRMLayout,
  },
  {
    path: "/crm/campaigns/$id",
    component: lazy(() => import("../Screens/CRM/CampaignDetail")),
    layout: CRMLayout,
  },
  {
    path: "/crm/contacts",
    component: lazy(() => import("../Screens/CRM/ContactsList")),
    layout: CRMLayout,
  },
  {
    path: "/crm/contacts/$id",
    component: lazy(() => import("../Screens/CRM/ContactDetail")),
    layout: CRMLayout,
  },
  {
    path: "/crm/inbox",
    component: lazy(() => import("../Screens/CRM/ConversationInbox")),
    layout: CRMLayout,
  },
  {
    path: "/crm/inbox/$id",
    component: lazy(() => import("../Screens/CRM/ConversationDetail")),
    layout: CRMLayout,
  },
  {
    path: "/crm/start-conversation",
    component: lazy(() => import("../Screens/CRM/StartConversation")),
    layout: CRMLayout,
    validateSearch: (search: any) => ({
      contactId: (search.contactId as string) || undefined,
    }),
  },
  {
    path: "/crm/templates",
    component: lazy(() => import("../Screens/CRM/TemplatesLibrary")),
    layout: CRMLayout,
  },
  {
    path: "/crm/analytics",
    component: lazy(() => import("../Screens/CRM/Analytics")),
    layout: CRMLayout,
  },
  {
    path: "/crm/email-accounts",
    component: lazy(() => import("../Screens/CRM/EmailAccounts")),
    layout: CRMLayout,
  },

  // =========================================================================
  // Content Gen
  // =========================================================================
  {
    path: "/content-ingestion",
    component: lazy(() => import("../Screens/ContentIngestionScreen")),
    category: "content",
    label: "Content Ingestion",
    icon: "📥",
    order: 0,
  },
  {
    path: "/content-management",
    component: lazy(() => import("../Screens/ContentManagementScreen")),
    category: "content",
    label: "Content Management",
    icon: "📝",
    order: 1,
    validateSearch: (search: any) => ({
      tab: (search.tab as string) || undefined,
      brand: (search.brand as string) || undefined,
    }),
  },
  {
    path: "/newsletter",
    component: lazy(() => import("../Screens/NewsletterScreen")),
    category: "content",
    label: "Newsletter",
    icon: "📧",
    order: 2,
  },

  // Newsletter editor (not in nav)
  {
    path: "/newsletter/$id",
    component: lazy(() => import("../components/Newsletter/NewsletterEditor")),
  },

  // =========================================================================
  // AI Tools
  // =========================================================================
  {
    path: "/ai-gateway",
    component: lazy(() => import("../Screens/AiGatewayScreen")),
    category: "ai-tools",
    label: "AI Gateway",
    icon: "🧪",
    order: 0,
  },
  {
    path: "/llm-playground",
    component: lazy(() => import("../Screens/LLMPlaygroundScreen/LLMPlaygroundScreen")),
    category: "ai-tools",
    label: "LLM Playground",
    icon: "🧪",
    order: 0,
  },
  {
    path: "/diagrammer",
    component: lazy(() => import("../Screens/DiagrammerScreen/DiagrammerScreen")),
    category: "ai-tools",
    label: "Diagrammer",
    icon: "🗺️",
    order: 1,
  },

  // Aura Builder group
  {
    path: "/aura",
    component: lazy(() => import("../Screens/AuraScreen/AuraScreen")),
    category: "ai-tools",
    label: "Projects",
    icon: "📁",
    parentGroup: "Aura Builder",
    order: 10,
  },
  {
    path: "/aura/templates",
    component: lazy(() => import("../Screens/AuraScreen/AuraTemplatesScreen")),
    category: "ai-tools",
    label: "Templates",
    icon: "📋",
    parentGroup: "Aura Builder",
    order: 11,
  },
  {
    path: "/aura/template-builder",
    component: lazy(() => import("../Screens/AuraScreen/AuraTemplateBuilderScreen")),
    category: "ai-tools",
    label: "Template Builder",
    icon: "🛠️",
    parentGroup: "Aura Builder",
    order: 12,
  },

  // Aura detail routes (not in nav)
  {
    path: "/aura/editor/$projectId",
    component: lazy(() => import("../Screens/AuraScreen/AuraEditorScreen")),
  },
  {
    path: "/aura/editor/$projectId/$pageSlug",
    component: lazy(() => import("../Screens/AuraScreen/AuraEditorScreen")),
  },

  {
    path: "/image-converter",
    component: lazy(() => import("../Screens/ImageConverter")),
    access: "public",
    category: "ai-tools",
    label: "Image Converter",
    icon: "🎨",
    order: 20,
  },
  {
    path: "/calendar-parser",
    component: lazy(() => import("../Screens/CalendarParser")),
    category: "ai-tools",
    label: "Calendar Parser",
    icon: "📅",
    order: 21,
  },
  {
    path: "/multi-language-test",
    component: lazy(() => import("../Screens/MultiLanguageTestAppScreen/MultiLanguageTestAppScreen")),
    category: "ai-tools",
    label: "Multi-Language Test",
    icon: "🚀",
    order: 22,
  },
  {
    path: "/databrain",
    component: lazy(() => import("../Screens/DatabrainScreen/DatabrainScreen")),
    category: "ai-tools",
    label: "Database Explorer",
    icon: "🗄️",
    order: 23,
  },
  {
    path: "/linkpacks",
    component: lazy(() =>
      import("../Screens/LinkPacksScreen/LinkPacksScreen").then((m) => ({ default: m.LinkPacksScreen })),
    ),
    access: "public",
    category: "ai-tools",
    label: "LinkPacks",
    icon: "🗂️",
    order: 24,
  },
  {
    path: "/linkpacks/$slug",
    component: lazy(() =>
      import("../Screens/LinkPacksScreen/LinkPackDetailScreen").then((m) => ({ default: m.LinkPackDetailScreen })),
    ),
    access: "public",
    label: "LinkPack",
  },

  // =========================================================================
  // Playground
  // =========================================================================
  {
    path: "/eas-dashboard",
    component: lazy(() =>
      import("../Screens/EASDashboardScreen/EASDashboardScreen").then((m) => ({ default: m.EASDashboardScreen })),
    ),
    category: "playground",
    label: "EAS Updates",
    icon: "📱",
    order: 0,
  },

  // EAS detail (not in nav)
  {
    path: "/eas-dashboard/$projectSlug",
    component: lazy(() =>
      import("../Screens/EASDashboardScreen/ProjectDashboard").then((m) => ({ default: m.ProjectDashboard })),
    ),
  },

  // Video Onboarding group
  {
    path: "/onboarding/journeys",
    component: lazy(() => import("../Screens/OnboardingScreen/JourneysListScreen")),
    category: "playground",
    label: "Learning Journeys",
    icon: "🗺️",
    parentGroup: "Video Onboarding",
    order: 10,
  },
  {
    path: "/onboarding/admin/videos",
    component: lazy(() => import("../Screens/OnboardingScreen/AdminVideosScreen")),
    category: "playground",
    label: "Manage Videos",
    icon: "📹",
    parentGroup: "Video Onboarding",
    order: 11,
  },
  {
    path: "/onboarding/admin/journeys",
    component: lazy(() => import("../Screens/OnboardingScreen/AdminJourneysScreen")),
    category: "playground",
    label: "Manage Journeys",
    icon: "✏️",
    parentGroup: "Video Onboarding",
    order: 12,
  },
  {
    path: "/onboarding/admin/questions",
    component: lazy(() => import("../Screens/OnboardingScreen/AdminQuestionsScreen")),
    category: "playground",
    label: "Questions Queue",
    icon: "❓",
    parentGroup: "Video Onboarding",
    order: 13,
  },

  // Onboarding detail routes (not in nav)
  {
    path: "/onboarding/journey/$journeyId",
    component: lazy(() => import("../Screens/OnboardingScreen/JourneyDetailScreen")),
  },
  {
    path: "/onboarding/video/$videoId",
    component: lazy(() => import("../Screens/OnboardingScreen/VideoPlayerScreen")),
  },

  // CoachOS group
  {
    path: "/coach-os",
    component: lazy(() => import("../Screens/CoachOSScreen/CoachOSScreen")),
    category: "playground",
    label: "Coach",
    icon: "🏃",
    parentGroup: "CoachOS",
    order: 20,
  },
  {
    path: "/trackables",
    component: lazy(() => import("../Screens/TrackablesScreen/TrackablesScreen")),
    category: "playground",
    label: "Trackables Admin",
    icon: "📊",
    parentGroup: "CoachOS",
    order: 21,
  },

  // CoachOS detail routes (not in nav)
  {
    path: "/coach-os/coach/$coachId",
    component: lazy(() => import("../Screens/CoachOSScreen/CoachDetailScreen")),
  },
  {
    path: "/coach-os/templates/$templateId",
    component: lazy(() => import("../Screens/CoachOSScreen/CoachTemplateDetailScreen")),
  },
  {
    path: "/coach-os/weekly-review",
    component: lazy(() => import("../Screens/CoachOSScreen/WeeklyReviewScreen")),
  },

  {
    path: "/movies",
    component: lazy(() => import("../Screens/MoviesScreen/MoviesScreen")),
    category: "playground",
    label: "Movie Discovery",
    icon: "🎬",
    order: 31,
    validateSearch: (search: any) => ({
      tab: (search.tab as string) || undefined,
      genre: (search.genre as string) || undefined,
      year: (search.year as string) || undefined,
    }),
  },
  {
    path: "/music",
    component: lazy(() => import("../Screens/MusicCatalogScreen")),
    category: "playground",
    label: "Music",
    icon: "🎵",
    order: 32,
  },
  {
    path: "/mashup",
    component: lazy(() => import("../Screens/MashupScreen/MashupScreen")),
    category: "playground",
    label: "DJ Mashup",
    icon: "🎧",
    order: 33,
  },
  {
    path: "/children-books",
    component: lazy(() => import("../Screens/ChildrenBooksScreen")),
    category: "playground",
    label: "Children Books",
    icon: "📚",
    order: 34,
  },
  {
    path: "/sports-wordle",
    component: lazy(() => import("../Screens/SportsWordleScreen")),
    category: "playground",
    label: "Sports Wordle",
    icon: "🏀",
    order: 35,
  },
  {
    path: "/shrays-hobbies",
    component: lazy(() => import("../Screens/ShraysHobbiesScreen")),
    category: "playground",
    label: "Shray's Hobbies",
    icon: "🎯",
    order: 37,
  },

  // AI Chat group
  {
    path: "/ai-projects",
    component: lazy(() => import("../Screens/AIProjectsScreen/AIProjectsScreen")),
    category: "playground",
    label: "Projects",
    icon: "📁",
    parentGroup: "AI Chat",
    order: 40,
  },
  {
    path: "/ai-chat",
    component: lazy(() => import("../Screens/AIChatHubScreen/AIChatHubScreen")),
    category: "playground",
    label: "Chat Hub",
    icon: "💬",
    parentGroup: "AI Chat",
    order: 41,
  },

  // Book reader (not in nav)
  {
    path: "/book/$bookId",
    component: lazy(() => import("../Screens/BookReaderScreen")),
  },

  // =========================================================================
  // AIX Infra
  // =========================================================================
  {
    path: "/agents",
    component: lazy(() => import("../Screens/AgentsScreen/AgentsScreen")),
    category: "aix-infra",
    label: "Agents",
    icon: "🤖",
    order: 0,
  },
  {
    path: "/agents/cockpit",
    component: lazy(() => import("../Screens/AgentsScreen/AgentsCockpitScreen")),
    category: "aix-infra",
    label: "Agent cockpit",
    icon: "🎛️",
    order: 1,
  },
  {
    path: "/agents/runs",
    component: lazy(() => import("../Screens/AgentsScreen/AgentsRunsHistoryScreen")),
    category: "aix-infra",
    label: "Agent runs",
    icon: "📜",
    order: 2,
  },
  {
    path: "/evals",
    component: lazy(() => import("../Screens/EvalsScreen/EvalsScreen")),
    category: "aix-infra",
    label: "Evals",
    icon: "🧪",
    order: 3,
  },
  {
    path: "/integrations",
    component: lazy(() => import("../Screens/IntegrationsScreen")),
    category: "aix-infra",
    label: "Integrations",
    icon: "🔗",
    order: 0,
  },
  {
    path: "/metrics",
    component: lazy(() => import("../Screens/MetricsScreen")),
    category: "aix-infra",
    label: "Metrics",
    icon: "📊",
    order: 1,
  },
  {
    path: "/notifications",
    component: lazy(() =>
      import("../Screens/NotificationScreen/NotificationScreen").then((m) => ({ default: m.NotificationScreen })),
    ),
    category: "aix-infra",
    label: "Notifications",
    icon: "🔔",
    order: 2,
  },
  {
    path: "/scraping",
    component: lazy(() => import("../Screens/ScrapingScreen")),
    category: "aix-infra",
    label: "Scraping",
    icon: "🕸️",
    order: 3,
    validateSearch: (search: any) => ({
      tab: (search.tab as string) || undefined,
      monitorSlug: (search.monitorSlug as string) || undefined,
      jobId: (search.jobId as string) || undefined,
    }),
  },
  {
    path: "/brain",
    component: lazy(() => import("../Screens/BrainScreen")),
    category: "aix-infra",
    label: "Brain Tasks",
    icon: "🧠",
    order: 4,
  },
  {
    path: "/cron",
    component: lazy(() => import("../Screens/CronScreen")),
    category: "aix-infra",
    label: "Scheduled Jobs",
    icon: "⏰",
    order: 5,
  },
  {
    path: "/devtools/monitors",
    component: lazy(() => import("../Screens/MonitorsScreen/MonitorsScreen")),
    category: "aix-infra",
    label: "API Monitors",
    icon: "🔍",
    order: 6,
  },
  {
    path: "/devtools/uptime",
    component: lazy(() => import("../Screens/UptimeMonitorsScreen/UptimeMonitorsScreen")),
    category: "aix-infra",
    label: "Uptime Monitors",
    icon: "📈",
    order: 6.5,
  },
  {
    path: "/devtools/vendor-status",
    component: lazy(() => import("../Screens/VendorStatusScreen/VendorStatusScreen")),
    category: "aix-infra",
    label: "Vendor Status",
    icon: "🏢",
    order: 7,
  },
  {
    path: "/devtools/deploy-feed",
    component: lazy(() => import("../Screens/DeployFeedScreen/DeployFeedScreen")),
    category: "aix-infra",
    label: "Deploy Feed",
    icon: "🚀",
    order: 8,
  },
  {
    path: "/devtools/incidents",
    component: lazy(() => import("../Screens/IncidentsScreen/IncidentsScreen")),
    category: "aix-infra",
    label: "Incidents",
    icon: "🚨",
    order: 8.5,
  },
  {
    path: "/devtools/incidents/$incidentId",
    component: lazy(() => import("../Screens/IncidentsScreen/IncidentDetailScreen")),
  },
  {
    path: "/devtools/secrets",
    component: lazy(() => import("../Screens/SecretsScreen")),
    category: "aix-infra",
    label: "Secrets",
    icon: "🔑",
    order: 9,
  },
  {
    path: "/reddit-growth",
    component: lazy(() => import("../Screens/RedditGrowthScreen/RedditGrowthScreen")),
    category: "ai-growth",
    label: "Reddit Growth",
    icon: "📈",
    order: 3,
    validateSearch: (search: Record<string, unknown>) => {
      const validTabs = new Set(["pipeline", "accounts", "metrics", "playbooks", "infra"])
      const tab = typeof search.tab === "string" && validTabs.has(search.tab) ? search.tab : undefined
      return { tab }
    },
  },

  // Monitor detail (not in nav)
  {
    path: "/devtools/uptime/$monitorId",
    component: lazy(() => import("../Screens/UptimeMonitorsScreen/UptimeMonitorDetailScreen")),
    label: "Uptime Monitor",
  },
  // Product PostHog pulse (not in nav; opened from the home Product pulse widget)
  {
    path: "/product/$company",
    component: lazy(() => import("../Screens/ProductPulseScreen/ProductPulseScreen")),
    label: "Product pulse",
  },
  {
    path: "/devtools/monitors/$scenarioId",
    component: lazy(() => import("../Screens/MonitorsScreen/ScenarioDetailScreen")),
  },

  // =========================================================================
  // Super Admin
  // =========================================================================
  {
    path: "/user-management",
    component: lazy(() => import("../Screens/UserManagementScreen")),
    category: "super-admin",
    label: "User Management",
    icon: "👤",
    order: 0,
  },
  {
    path: "/companies",
    component: lazy(() =>
      import("../Screens/CompaniesScreen/CompaniesScreen").then((m) => ({ default: m.CompaniesScreen })),
    ),
    category: "super-admin",
    label: "Companies",
    icon: "🏢",
    order: 1,
  },
  {
    path: "/audit-logs",
    component: lazy(() => import("../Screens/AuditLogsScreen")),
    category: "super-admin",
    label: "Audit Logs",
    icon: "📋",
    order: 2,
    validateSearch: (search: any) => ({
      action: (search.action as string) || undefined,
      entityType: (search.entityType as string) || undefined,
      userId: (search.userId as string) || undefined,
      entityId: (search.entityId as string) || undefined,
    }),
  },

  // Company detail (not in nav)
  {
    path: "/companies/$slug",
    component: lazy(() =>
      import("../Screens/CompaniesScreen/CompanyDetailScreen").then((m) => ({ default: m.CompanyDetailScreen })),
    ),
  },

  // =========================================================================
  // Auth & utility routes (bare — no ProtectedRoute/PublicRoute wrapper)
  // =========================================================================
  {
    path: "/auth",
    component: lazy(() => import("../Screens/AuthScreen")),
    access: "bare",
    validateSearch: (search: Record<string, unknown>) => ({
      redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    }),
  },
  {
    path: "/auth/callback",
    component: lazy(() => import("../Screens/AuthCallbackScreen")),
    access: "bare",
    validateSearch: (search: Record<string, unknown>) => ({
      redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    }),
  },
  {
    path: "/admin/signin",
    component: lazy(() => import("../Screens/AdminSigninScreen")),
    access: "bare",
  },
  {
    path: "/slack/callback",
    component: lazy(() => import("../Screens/SlackCallbackScreen")),
    access: "bare",
  },
  {
    path: "/access-denied",
    component: lazy(() => import("../Screens/AccessDeniedScreen")),
    access: "bare",
  },
  {
    path: "/signout",
    component: lazy(() => import("../Screens/SignOutScreen")),
    access: "bare",
  },

  // =========================================================================
  // Public routes
  // =========================================================================
  {
    path: "/status",
    component: lazy(() => import("../Screens/StatusPage/StatusPage")),
    access: "public",
  },

  // =========================================================================
  // Misc routes (not in nav, not in any category)
  // =========================================================================
  {
    path: "/meta-ads",
    component: lazy(() => import("../Screens/MetaAdsScreen/MetaAdsScreen").then((m) => ({ default: m.MetaAdsScreen }))),
    validateSearch: (search: any) => ({
      accountId: (search.accountId as string) || undefined,
    }),
  },
  {
    path: "/meta-ads/campaigns/$campaignId",
    component: lazy(() => import("../Screens/MetaAdsScreen/MetaAdsScreen").then((m) => ({ default: m.MetaAdsScreen }))),
  },
]

// ---------------------------------------------------------------------------
// Group icon map (used by navigationConfig to assign icons to parent groups)
// ---------------------------------------------------------------------------
